import React, { useLayoutEffect, useRef } from 'react';

import type { EditingMode } from '@aline/common';
import {
  AlineTransforms,
  HistoryTransforms,
  listDefinitionPresets,
  Queries,
  SelEventSource,
} from '@aline/common';
import { link } from '@aline/routes';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { keys } from 'ramda';
import type { NavigateFunction } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Editor, Range } from 'slate';
import type { ActiveCommentPanel } from 'src/editor/editor-tab-state-registry';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { openImageDialogWithImage } from 'src/editor/open-image-dialog';
import { attachInsertLinkPopper } from 'src/editor/toolbars';
import { ClientTransforms } from 'src/editor/transforms';
import { AlineClipboardDataSchema } from 'src/editor/transforms/clipboard-schemas';
import {
  CLIPBOARD_ALINE_DATA_ATTR,
  dataAlineFragmentWrapper,
} from 'src/editor/transforms/general-transforms';
import { useCurrentUserId } from 'src/gql';
import { useDocumentParams } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import { createHotkeys } from 'src/helpers/hotkeys';
import { getAuthorizedTransformsForEditor, useGetEditorUserState } from 'src/hooks';
import type { AlineClientEditor } from 'src/layout-engine/hooks';
import { useEditor } from 'src/layout-engine/hooks';
import { ReactEditor } from 'src/layout-engine/react-editor';
import { EDITOR_FOCUS, EDITOR_TO_INPUT_HANDLER } from 'src/layout-engine/utils/weak-maps';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { pushNotification } from 'src/notifications';
import { useEditingDisabled } from 'src/store/document/selectors';

export const editorHotkeys = createHotkeys<{
  authorId: UUID;
  client: ApolloClient;
  documentId: UUID;
  editingMode: EditingMode;
  editor: AlineClientEditor;
  navigate: NavigateFunction;
  openDrawer: () => void;
  setActiveCommentPanel: (activePanel: ActiveCommentPanel) => void;
}>()({
  alignCenter: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
        alignment: 'center',
      });
    },
    apple: { hint: '⇧⌘E' },
    key: 'mod+shift+e',
    pc: { hint: 'Ctrl+Shift+E' },
  },
  alignLeft: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, { alignment: 'left' });
    },
    apple: { hint: '⇧⌘L' },
    key: 'mod+shift+l',
    pc: { hint: 'Ctrl+Shift+L' },
  },
  alignRight: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, { alignment: 'right' });
    },
    apple: { hint: '⇧⌘R' },
    key: 'mod+shift+r',
    pc: { hint: 'Ctrl+Shift+R' },
  },
  bold: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleTextFormatting(editor, {
        authorId,
        editingMode,
        formatKey: 'bold',
      });
    },
    apple: { hint: '⌘B' },
    key: 'mod+b',
    pc: { hint: 'Ctrl+B' },
  },
  clearFormatting: { apple: { hint: '⌘\\' }, pc: { hint: 'Ctrl+\\' } },
  copy: {
    apple: { hint: '⌘C' },
    pc: { hint: 'Ctrl+C' },
  },
  cut: {
    apple: { hint: '⌘X' },
    pc: { hint: 'Ctrl+X' },
  },
  decreaseFontSize: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.decrementFontSize(editor, { authorId, editingMode });
    },
    apple: { hint: '⇧⌘,' },
    key: 'mod+shift+,',
    pc: { hint: 'Ctrl+Shift+,' },
  },
  decreaseIndentation: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.decreaseIndentation(editor, { authorId, editingMode });
    },
    apple: { hint: '⌘[' },
    key: 'mod+[',
    pc: { hint: 'Ctrl+[' },
  },
  deleteBackward: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.deleteContent(editor, {
        authorId,
        direction: 'backward',
        editingMode,
      });
    },
    apple: { key: ['backspace', 'shift+backspace', 'ctrl+backspace'] },
    pc: { key: ['backspace', 'shift+backspace'] },
  },
  deleteForward: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.deleteContent(editor, {
        authorId,
        direction: 'forward',
        editingMode,
      });
    },
    key: ['ctrl+d', 'delete'],
  },
  deleteToLineStart: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AlineTransforms.withoutNormalizing(editor, () => {
        if (editor.selection && Range.isCollapsed(editor.selection)) {
          AlineTransforms.moveSelToLineEdge(editor, 'start', { point: 'focus' });
        }
        AuthorizedTransforms.deleteContent(editor, {
          authorId,
          direction: 'backward',
          editingMode,
        });
      });
    },
    apple: { key: 'cmd+backspace' },
  },
  deleteWordBackward: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AlineTransforms.withoutNormalizing(editor, () => {
        if (editor.selection && Range.isCollapsed(editor.selection)) {
          AlineTransforms.moveSel(editor, {
            distance: 1,
            edge: 'focus',
            reverse: true,
            // Word deletion stops at the cell boundary rather than selecting the whole cell first.
            selectWholeCellAtEdge: false,
            unit: 'word',
          });
        }
        AuthorizedTransforms.deleteContent(editor, {
          authorId,
          direction: 'backward',
          editingMode,
        });
      });
    },
    apple: { key: 'option+backspace' },
    pc: { key: 'ctrl+backspace' },
  },
  deleteWordForward: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AlineTransforms.withoutNormalizing(editor, () => {
        if (editor.selection && Range.isCollapsed(editor.selection)) {
          AlineTransforms.moveSel(editor, {
            distance: 1,
            edge: 'focus',
            reverse: false,
            // Word deletion stops at the cell boundary rather than selecting the whole cell first.
            selectWholeCellAtEdge: false,
            unit: 'word',
          });
        }
        AuthorizedTransforms.deleteContent(editor, {
          authorId,
          direction: 'forward',
          editingMode,
        });
      });
    },
    apple: { key: 'option+delete' },
    pc: { key: 'ctrl+delete' },
  },
  expandSelectionToDocumentEnd: {
    action: ({ editor }): void => {
      if (!editor.selection) return;
      const entryAbove = Queries.selectionBoundaryAboveLoc(editor);
      if (!entryAbove) return;
      const [, pathAbove] = entryAbove;
      const end = Editor.end(editor, pathAbove);
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.KEYBOARD,
        props: { ...editor.selection, focus: end },
      });
    },
    apple: { key: 'cmd+shift+down' },
    pc: { key: 'ctrl+shift+end' },
  },
  expandSelectionToDocumentStart: {
    action: ({ editor }): void => {
      if (!editor.selection) return;
      const entryAbove = Queries.selectionBoundaryAboveLoc(editor);
      if (!entryAbove) return;
      const [, pathAbove] = entryAbove;
      const start = Editor.start(editor, pathAbove);
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.KEYBOARD,
        props: { ...editor.selection, focus: start },
      });
    },
    apple: { key: 'cmd+shift+up' },
    pc: { key: 'ctrl+shift+home' },
  },
  expandSelectionToLineEnd: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelToLineEdge(editor, 'end', { point: 'focus' }),
    apple: { key: 'cmd+shift+right' },
    pc: { key: 'shift+end' },
  },
  expandSelectionToLineStart: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelToLineEdge(editor, 'start', { point: 'focus' }),
    apple: { key: 'cmd+shift+left' },
    pc: { key: 'shift+home' },
  },
  expandSelectionToNextCharacter: {
    action: ({ editor }): void =>
      AlineTransforms.moveSel(editor, {
        distance: 1,
        edge: 'focus',
        reverse: false,
        unit: 'character',
      }),
    key: 'shift+right',
  },
  expandSelectionToNextLine: {
    action: ({ authorId, editingMode, editor }): void =>
      ClientTransforms.moveSelVertically(editor, {
        authorId,
        direction: 'forward',
        edge: 'focus',
        editingMode,
      }),
    key: 'shift+down',
  },
  expandSelectionToNextWord: {
    action: ({ editor }): void =>
      AlineTransforms.moveSel(editor, {
        distance: 1,
        edge: 'focus',
        reverse: false,
        unit: 'word',
      }),
    apple: { key: 'option+shift+right' },
    pc: { key: 'ctrl+shift+right' },
  },
  expandSelectionToPreviousCharacter: {
    action: ({ editor }): void =>
      AlineTransforms.moveSel(editor, {
        distance: 1,
        edge: 'focus',
        reverse: true,
        unit: 'character',
      }),
    key: 'shift+left',
  },
  expandSelectionToPreviousLine: {
    action: ({ authorId, editingMode, editor }): void =>
      ClientTransforms.moveSelVertically(editor, {
        authorId,
        direction: 'backward',
        edge: 'focus',
        editingMode,
      }),
    key: 'shift+up',
  },
  expandSelectionToPreviousWord: {
    action: ({ editor }): void =>
      AlineTransforms.moveSel(editor, {
        distance: 1,
        edge: 'focus',
        reverse: true,
        unit: 'word',
      }),
    apple: { key: 'option+shift+left' },
    pc: { key: 'ctrl+shift+left' },
  },
  increaseFontSize: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.incrementFontSize(editor, { authorId, editingMode });
    },
    apple: { hint: '⇧⌘.' },
    key: 'mod+shift+.',
    pc: { hint: 'Ctrl+Shift+.' },
  },
  increaseIndentation: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.increaseIndentation(editor, { authorId, editingMode });
    },
    apple: { hint: '⌘]' },
    key: 'mod+]',
    pc: { hint: 'Ctrl+]' },
  },
  insertBreak: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.insertBreak(editor, { authorId, editingMode });
    },
    key: 'enter',
  },
  insertComment: {
    action: ({ documentId, navigate, openDrawer, setActiveCommentPanel }): void => {
      setActiveCommentPanel('AddComment');
      navigate(link.DocumentDetailComments({ documentId }), { replace: true });
      openDrawer();
    },
    apple: { hint: '⌥⌘M' },
    key: 'mod+alt+m',
    pc: { hint: 'Ctrl+Alt+M' },
  },
  insertPageBreak: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.insertPageBreak(editor, { authorId, editingMode });
    },
    apple: { hint: '⌘↵' },
    key: 'mod+enter',
    pc: { hint: 'Ctrl+Enter' },
  },
  italicize: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleTextFormatting(editor, {
        authorId,
        editingMode,
        formatKey: 'italic',
      });
    },
    apple: { hint: '⌘I' },
    key: 'mod+i',
    pc: { hint: 'Ctrl+I' },
  },
  moveToDocumentEnd: {
    action: ({ editor }): void => AlineTransforms.selectDocPartEdge(editor, { edge: 'end' }),
    apple: { key: 'cmd+down' },
    pc: { key: 'ctrl+end' },
  },
  moveToDocumentStart: {
    action: ({ editor }): void => AlineTransforms.selectDocPartEdge(editor, { edge: 'start' }),
    apple: { key: 'cmd+up' },
    pc: { key: 'ctrl+home' },
  },
  moveToLineEnd: {
    action: ({ editor }): void => AlineTransforms.moveSelToLineEdge(editor, 'end'),
    apple: { key: 'cmd+right' },
    pc: { key: 'end' },
  },
  moveToLineStart: {
    action: ({ editor }): void => AlineTransforms.moveSelToLineEdge(editor, 'start'),
    apple: { key: 'cmd+left' },
    pc: { key: 'home' },
  },
  moveToNextCharacter: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelHorizontally(editor, { direction: 'forward' }),
    key: 'right',
  },
  moveToNextLine: {
    action: ({ authorId, editingMode, editor }): void =>
      ClientTransforms.moveSelVertically(editor, {
        authorId,
        direction: 'forward',
        edge: 'both',
        editingMode,
      }),
    key: 'down',
  },
  moveToNextWord: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelHorizontally(editor, { direction: 'forward', unit: 'word' }),
    apple: { key: 'option+right' },
    pc: { key: 'ctrl+right' },
  },
  moveToParagraphEnd: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelToParagraphEdge(editor, { direction: 'forward', edge: 'both' }),
    apple: { key: ['ctrl+e', 'option+down'] },
    pc: { key: 'ctrl+down' },
  },
  moveToParagraphStart: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelToParagraphEdge(editor, { direction: 'backward', edge: 'both' }),
    apple: { key: ['ctrl+a', 'option+up'] },
    pc: { key: 'ctrl+up' },
  },
  moveToPreviousCharacter: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelHorizontally(editor, { direction: 'backward' }),
    key: 'left',
  },
  moveToPreviousLine: {
    action: ({ authorId, editingMode, editor }): void =>
      ClientTransforms.moveSelVertically(editor, {
        authorId,
        direction: 'backward',
        edge: 'both',
        editingMode,
      }),
    key: 'up',
  },
  moveToPreviousWord: {
    action: ({ editor }): void =>
      AlineTransforms.moveSelHorizontally(editor, { direction: 'backward', unit: 'word' }),
    apple: { key: 'option+left' },
    pc: { key: 'ctrl+left' },
  },
  openLinkPopper: {
    action: ({ editor }): void => {
      ReactEditor.blur(editor);
      attachInsertLinkPopper(editor);
    },
    apple: { hint: '⌘K' },
    key: 'mod+k',
    pc: { hint: 'Ctrl+K' },
  },
  paste: {
    apple: { hint: '⌘V' },
    pc: { hint: 'Ctrl+V' },
  },
  redo: {
    action: ({ editor }): void => HistoryTransforms.redo(editor),
    apple: { hint: '⇧⌘Z' },
    key: 'mod+shift+z',
    pc: { hint: 'Ctrl+Shift+Z' },
  },
  selectAll: {
    action: ({ editor }): void => AlineTransforms.selectAll(editor),
    apple: { hint: '⌘A' },
    key: 'mod+a',
    pc: { hint: 'Ctrl+A' },
  },
  strikethrough: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleTextFormatting(editor, {
        authorId,
        editingMode,
        formatKey: 'strikethrough',
      });
    },
    apple: { hint: '⇧⌘X', key: 'cmd+shift+x' },
    pc: { hint: 'Alt+Shift+5', key: 'alt+shift+5' }, // From Google Docs
  },
  styleHeading1: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
        variant: 'style-heading1',
      });
    },
    apple: { hint: '⌥⌘1', key: 'cmd+opt+1' },
    pc: { hint: 'Ctrl+Shift+1', key: 'ctrl+shift+1' },
  },
  styleHeading2: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
        variant: 'style-heading2',
      });
    },
    apple: { hint: '⌥⌘2', key: 'cmd+opt+2' },
    pc: { hint: 'Ctrl+Shift+2', key: 'ctrl+shift+2' },
  },
  styleHeading3: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
        variant: 'style-heading3',
      });
    },
    apple: { hint: '⌥⌘3', key: 'cmd+opt+3' },
    pc: { hint: 'Ctrl+Shift+3', key: 'ctrl+shift+3' },
  },
  styleNormal: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
        variant: 'style-normal',
      });
    },
    apple: { hint: '⌥⌘0', key: 'cmd+opt+0' },
    pc: { hint: 'Ctrl+Shift+0', key: 'ctrl+shift+0' },
  },
  subscript: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleVerticalAlign(editor, {
        authorId,
        editingMode,
        verticalAlign: 'subscript',
      });
    },
    apple: { hint: '⌘,' },
    key: 'mod+,',
    pc: { hint: 'Ctrl+,' },
  },
  superscript: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleVerticalAlign(editor, {
        authorId,
        editingMode,
        verticalAlign: 'superscript',
      });
    },
    apple: { hint: '⌘.' },
    key: 'mod+.',
    pc: { hint: 'Ctrl+.' },
  },
  tab: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.tab(editor, {
        authorId,
        direction: 'forward',
        editingMode,
      });
    },
    apple: { hint: '⇥' },
    key: 'tab',
    pc: { hint: 'Tab' },
  },
  tabReverse: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.tab(editor, {
        authorId,
        direction: 'backward',
        editingMode,
      });
    },
    apple: { hint: '⇧⇥' },
    key: 'shift+tab',
    pc: { hint: 'Shift+Tab' },
  },
  toggleBulletedList: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleListItems(editor, {
        authorId,
        editingMode,
        listDefinition: listDefinitionPresets[6],
      });
    },
    apple: { hint: '⇧⌘8' },
    key: 'mod+shift+8',
    pc: { hint: 'Ctrl+Shift+8' },
  },
  toggleNumberedList: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleListItems(editor, {
        authorId,
        editingMode,
        listDefinition: listDefinitionPresets[0],
      });
    },
    apple: { hint: '⇧⌘7' },
    key: 'mod+shift+7',
    pc: { hint: 'Ctrl+Shift+7' },
  },
  underline: {
    action: ({ authorId, editingMode, editor }): void => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
      AuthorizedTransforms.toggleTextFormatting(editor, {
        authorId,
        editingMode,
        formatKey: 'underline',
      });
    },
    apple: { hint: '⌘U' },
    key: 'mod+u',
    pc: { hint: 'Ctrl+U' },
  },
  undo: {
    action: ({ editor }): void => HistoryTransforms.undo(editor),
    apple: { hint: '⌘Z' },
    key: 'mod+z',
    pc: { hint: 'Ctrl+Z' },
  },
});

function handleInput(
  editor: Editor,
  editingMode: EditingMode | null,
  authorId: UUID | null,
  event: React.InputEvent<HTMLDivElement>
): void {
  event.preventDefault();

  if (!editingMode || !authorId) return;
  if (event.nativeEvent.data) {
    const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
    AuthorizedTransforms.insertTextAtSelection(
      editor,
      editingMode,
      authorId,
      event.nativeEvent.data
    );
  }
}

function handleCut(
  editor: Editor,
  options: {
    authorId: UUID | null;
    documentId: UUID;
    editingMode: EditingMode | null;
    event: React.ClipboardEvent<HTMLDivElement>;
  }
): void {
  const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);

  AlineTransforms.withoutNormalizing(editor, async () => {
    const { authorId, documentId, editingMode, event } = options;

    event.preventDefault();

    if (!editingMode || !authorId) return;

    const isCopied = await ClientTransforms.copyToClipboard(editor, { documentId });
    if (isCopied) {
      AuthorizedTransforms.deleteContent(editor, { authorId, editingMode });
    }
  });
}

const editorHotkeyKeys = keys(editorHotkeys);

/**
 * Custom MIME types must be prefixed with "web " (including space) to be read by the clipboard API.
 * See https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem/supports_static.
 */
export const ClipboardMime = {
  alineSelection: 'web application/x-aline-selection',
  html: 'text/html',
  slate: 'web application/x-aline-fragment',
  text: 'text/plain',
} as const;

/**
 * Encodes a Slate fragment for storage in HTML data attribute or custom MIME type.
 */
export function encodeSlateFragment(fragmentJSON: string): string {
  return window.btoa(encodeURIComponent(fragmentJSON));
}

/**
 * Decodes a Slate fragment from clipboard data.
 */
export function decodeSlateFragment(encoded: string): string {
  return decodeURIComponent(window.atob(encoded));
}

/**
 * Gets clipboard data from either a clipboard event or the navigator.clipboard API.
 * - Fallback for browsers (like Firefox) that don't support the custom Slate MIME type:
 *   - Reads the Slate fragment from a `data-slate-fragment` attribute in the HTML if present
 *   - Decodes it and returns as clipboardSlate
 */
export async function getClipboardData(): Promise<{
  clipboardAlineSelection: string;
  clipboardHTML: string;
  clipboardSlate: string;
  clipboardText: string;
  clipboardDocumentId?: UUID;
  clipboardImage?: Blob;
} | null> {
  try {
    const clipboardItems = await navigator.clipboard.read();

    const htmlItem = clipboardItems.find(item => item.types.includes(ClipboardMime.html));
    const textItem = clipboardItems.find(item => item.types.includes(ClipboardMime.text));
    const slateItem = clipboardItems.find(item => item.types.includes(ClipboardMime.slate));
    const alineSelectionItem = clipboardItems.find(item =>
      item.types.includes(ClipboardMime.alineSelection)
    );
    const imageItem = clipboardItems.find(item =>
      item.types.some(type => type.startsWith('image/'))
    );
    const imageType = imageItem?.types.find(type => type.startsWith('image/'));

    const [clipboardHTML, clipboardText, clipboardSlate, clipboardAlineSelection, clipboardImage] =
      await Promise.all([
        htmlItem ? htmlItem.getType(ClipboardMime.html).then(blob => blob.text()) : '',
        textItem ? textItem.getType(ClipboardMime.text).then(blob => blob.text()) : '',
        slateItem ? slateItem.getType(ClipboardMime.slate).then(blob => blob.text()) : '',
        alineSelectionItem
          ? alineSelectionItem.getType(ClipboardMime.alineSelection).then(blob => blob.text())
          : '',
        imageType ? imageItem?.getType(imageType) : undefined,
      ]);
    const extracted = extractSlateFragmentFromHTML(clipboardHTML);
    const clipboardDocumentId = extracted?.documentId;
    const canUseNavigatorCustomMimeTypes = Boolean(clipboardAlineSelection || clipboardSlate);
    const clipboardData = {
      clipboardAlineSelection,
      clipboardDocumentId,
      clipboardHTML,
      clipboardSlate,
      clipboardText,
    };
    // Indicates this browser supports the custom MIME types for the clipboard API.
    if (canUseNavigatorCustomMimeTypes) return clipboardData;

    return extracted
      ? {
          ...clipboardData,
          clipboardAlineSelection: extracted.alineSelection,
          clipboardHTML: extracted.html,
          clipboardImage,
          clipboardSlate: extracted.slate,
          clipboardText,
        }
      : { clipboardAlineSelection, clipboardHTML, clipboardImage, clipboardSlate, clipboardText };
  } catch (error) {
    const isNotAllowedError = error instanceof Error && error.name === 'NotAllowedError';
    const errorMessage = isNotAllowedError
      ? 'Unable to access clipboard. Please ensure you have granted clipboard permissions.'
      : 'Failed to get clipboard data. Contact support if the issue persists.';
    pushNotification({
      message: errorMessage,
      variant: 'error',
    });
    console.error('Error getting clipboard data:', error);
    return null;
  }
}

/**
 * Extracts Slate fragment and Aline selection data from HTML clipboard data.
 * Uses consolidated data-aline-clipboard attribute with Zod validation.
 * Returns null if no data is found or extraction fails.
 */
function extractSlateFragmentFromHTML(
  html: string
): { alineSelection: string; html: string; slate: string; documentId?: UUID } | null {
  try {
    const tempContainerElement = document.createElement('div');
    tempContainerElement.innerHTML = html;

    const clipboardElement = tempContainerElement.querySelector(`[${CLIPBOARD_ALINE_DATA_ATTR}]`);
    if (!clipboardElement) return null;

    const clipboardDataStr = clipboardElement.getAttribute(CLIPBOARD_ALINE_DATA_ATTR);
    if (!clipboardDataStr) return null;

    // Parse and validate using Zod schema
    const clipboardData = AlineClipboardDataSchema.parse(JSON.parse(clipboardDataStr));

    const slate = decodeSlateFragment(clipboardData.slate);
    const alineSelection = clipboardData.selection || '';

    // Remove wrapper div if present
    const unwrappedHTML = clipboardElement.hasAttribute(dataAlineFragmentWrapper)
      ? clipboardElement.innerHTML
      : html;

    return { alineSelection, documentId: clipboardData.documentId, html: unwrappedHTML, slate };
  } catch (err) {
    console.warn('Failed to extract clipboard data from HTML:', err);
    return null;
  }
}

export async function handlePaste(
  editor: Editor,
  options: {
    authorId: string;
    documentId: UUID;
    editingMode: EditingMode;
  }
): Promise<void> {
  const { authorId, documentId, editingMode } = options;

  const clipboardData = await getClipboardData();
  if (!clipboardData) return;
  const { clipboardDocumentId, clipboardHTML, clipboardImage, clipboardSlate, clipboardText } =
    clipboardData;

  const stripCommentIds = clipboardDocumentId !== documentId;
  const pasteContext = {
    sourceDocumentId: clipboardDocumentId,
    targetDocumentId: documentId,
  };

  const hasInserted = await AlineTransforms.withoutNormalizing(
    editor,
    async (): Promise<boolean> => {
      const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);

      if (clipboardSlate) {
        const insertedNodes = AuthorizedTransforms.insertContentNodes(editor, {
          authorId,
          editingMode,
          insertSource: 'paste',
          nodes: JSON.parse(clipboardSlate),
          pasteContext,
          select: 'end',
          stripCommentIds,
        });

        if (insertedNodes) {
          const insertedString = Queries.nodeString(insertedNodes, { paragraphSeparator: '\n' });
          AlineTransforms.announce(editor, `Pasted ${insertedString}`);
          return true;
        }
      }

      if (clipboardHTML) {
        const insertedNodes = AlineTransforms.insertHTML(editor, {
          authorId,
          editingMode,
          html: clipboardHTML,
          pasteContext,
          stripCommentIds,
        });

        if (insertedNodes) {
          const insertedString = Queries.nodeString(insertedNodes, { paragraphSeparator: '\n' });
          AlineTransforms.announce(editor, `Pasted ${insertedString}`);
          return true;
        }
      }

      if (clipboardText) {
        AuthorizedTransforms.insertPlaintext(editor, {
          authorId,
          editingMode,
          select: true,
          text: clipboardText,
        });
        AlineTransforms.announce(editor, `Pasted ${clipboardText}`);
        return true;
      }

      return false;
    }
  );

  /**
   * Do not try to insert an image if we've already inserted text content. This ensures that we
   * don't insert the image of the copied text Word includes on the clipboard when copying from it.
   */
  if (!hasInserted && clipboardImage) {
    openImageDialogWithImage(clipboardImage);
    return;
  }
}

export function KeyboardInputHandler(): JSX.Element {
  const editor = useEditor();
  const client = useApolloClient();
  const navigate = useNavigate();
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();
  const {
    comments: { setActivePanel },
  } = useEditorTabStateActions();
  const contenteditable = useRef<HTMLDivElement>(null);
  const { isInSigningCeremony, isReadOnly } = useGetEditorUserState();
  /**
   * Combine `useEditingDisabled()` (which reads Redux editingMode + loading)
   * with the direct workflow flags so cut / paste / typing / hotkeys can't
   * mutate the document during the brief window between `isInSigningCeremony`
   * (or `isReadOnly`) flipping true and AlineEditor dispatching
   * `SET_EDITING_MODE → VIEWING`.
   */
  const isInputDisabled = useEditingDisabled() || isInSigningCeremony || isReadOnly;
  const isContentEditable = !(IS_MOBILE && isInputDisabled);

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);

  useSetInputHandler(editor, contenteditable);

  return (
    <div
      ref={contenteditable}
      data-keyboard-input-handler
      suppressContentEditableWarning
      contentEditable={isContentEditable}
      style={{ height: 1, left: -10000, position: 'absolute', top: -10000, width: 1 }}
      onBlur={() => {
        // Do not call blur if user is switching to another tab or application
        if (document.hasFocus()) {
          ReactEditor.blur(editor);
        }
      }}
      onCopy={() => ClientTransforms.copyToClipboard(editor, { documentId })}
      onCut={event => {
        if (isInputDisabled) return;

        handleCut(editor, {
          authorId: currentUserId,
          documentId,
          editingMode,
          event,
        });
        ClientTransforms.focus(editor);
      }}
      onInput={(event: React.InputEvent<HTMLDivElement>) => {
        if (isInputDisabled) return;

        handleInput(editor, editingMode, currentUserId, event);

        ClientTransforms.focus(editor);
      }}
      onKeyDown={event => {
        if (!currentUserId || !editingMode || isInputDisabled) return;

        for (const key of editorHotkeyKeys) {
          const hotkey = editorHotkeys[key];

          if (hotkey.action && hotkey.test?.(event.nativeEvent)) {
            event.preventDefault();
            hotkey.action({
              authorId: currentUserId,
              client,
              documentId,
              editingMode,
              editor,
              navigate,
              openDrawer,
              setActiveCommentPanel: setActivePanel,
            });
            break;
          }
        }
      }}
      onPaste={async event => {
        if (!currentUserId || !editingMode || isInputDisabled) return;
        event.preventDefault();
        await handlePaste(editor, { authorId: currentUserId, documentId, editingMode });
        ClientTransforms.focus(editor);
      }}
    />
  );
}

function useSetInputHandler(
  editor: Editor,
  contenteditable: React.RefObject<HTMLDivElement | null>
): void {
  useLayoutEffect(() => {
    const editableElement = contenteditable.current;
    if (!editableElement) return;

    EDITOR_TO_INPUT_HANDLER.set(editor, editableElement);
    if (EDITOR_FOCUS.get(editor)?.isFocused && window.document.activeElement !== editableElement) {
      editableElement.focus({ preventScroll: true });
    }
  }, [editor, contenteditable]);
}
